// Lightweight Markdown → React nodes (bold, italic, code, links, line breaks)

import React from 'react';

import { Anchor } from '@atlaskit/primitives/compiled';
import { Text } from '@atlaskit/primitives/compiled';

type MarkdownNode = {
	content: string;
	href?: string;
	type: 'text' | 'bold' | 'italic' | 'code' | 'link';
};

function tokenize(text: string): MarkdownNode[] {
	const nodes: MarkdownNode[] = [];
	// Match: **bold**, *italic*, `code`, [text](url)
	const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[(.+?)\]\((.+?)\))/g;
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	while ((match = pattern.exec(text)) !== null) {
		// Text before match
		if (match.index > lastIndex) {
			nodes.push({ type: 'text', content: text.slice(lastIndex, match.index) });
		}

		if (match[2]) {
			nodes.push({ type: 'bold', content: match[2] });
		} else if (match[3]) {
			nodes.push({ type: 'italic', content: match[3] });
		} else if (match[4]) {
			nodes.push({ type: 'code', content: match[4] });
		} else if (match[5] && match[6]) {
			nodes.push({ type: 'link', content: match[5], href: match[6] });
		}

		lastIndex = match.index + match[0].length;
	}

	// Remaining text
	if (lastIndex < text.length) {
		nodes.push({ type: 'text', content: text.slice(lastIndex) });
	}

	return nodes;
}

export function renderMarkdown(text: string): React.ReactNode {
	const nodes = tokenize(text);
	if (nodes.length === 0) {
		return text;
	}
	if (nodes.length === 1 && nodes[0].type === 'text') {
		return text;
	}

	return nodes.map((node, index) => {
		switch (node.type) {
			case 'bold':
				return (
					<Text key={index} as="span" weight="bold">
						{node.content}
					</Text>
				);
			case 'italic':
				return (
					<Text key={index} as="em">
						{node.content}
					</Text>
				);
			case 'code':
				return <code key={index}>{node.content}</code>;
			case 'link':
				return (
					<Anchor key={index} href={node.href ?? ''} target="_blank" rel="noopener noreferrer">
						{node.content}
					</Anchor>
				);
			default:
				return <React.Fragment key={index}>{node.content}</React.Fragment>;
		}
	});
}
